import { collection, getDocs, query, where, orderBy, writeBatch, doc, serverTimestamp, limit } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "../services/collections";
import { toISODate } from "../utils/date";

/**
 * SessionService
 * Centraliza as operações de banco de dados para o módulo de Sessões.
 * Coleção: tenants/{idTenant}/sessions
 * 
 * REGRA DE NEGÓCIO:
 * Cada documento de sessão = 1 aula em 1 dia específico.
 * Sessões são geradas automaticamente a partir de uma turma (class).
 * Gera sessões para os próximos 6 meses (180 dias).
 */

const MONTHS_AHEAD = 6;

/**
 * Calcula a data limite para geração de sessões (hoje + 6 meses).
 */
const getSessionHorizon = (fromDate = new Date()) => {
    const horizon = new Date(fromDate);
    horizon.setMonth(horizon.getMonth() + MONTHS_AHEAD);
    return horizon;
};

const SessionService = {
    /**
     * Gera sessões para uma turma para os próximos 6 meses.
     * 
     * CUSTO FIREBASE:
     * - Se existingEnrollments=[] (criação): 0 leituras extras, ~26 escritas
     * - Se existingEnrollments=null (extensão): 1 query + N leituras de enrollments, ~26 escritas
     * 
     * @param {Object} classData - Dados da turma (já salvos no Firestore)
     * @param {string} classId - ID do documento da turma
     * @param {string} idTenant - ID do tenant
     * @param {Array|null} existingEnrollments - Matrículas ativas já conhecidas.
     *   Passar [] para turma nova (evita query). Passar null/undefined para buscar do Firestore.
     * @returns {number} Quantidade de sessões criadas
     */
    generateSessionsForClass: async (classData, classId, idTenant, existingEnrollments = null) => {
        if (!idTenant || !classId) throw new Error("Tenant e Class ID são obrigatórios.");

        const weekday = Number(classData.weekday);
        const startDateStr = classData.startDate || toISODate(new Date());
        const endDateStr = classData.endDate || null;

        const startDate = new Date(startDateStr + "T00:00:00");
        const horizon = getSessionHorizon();
        const endLimit = endDateStr ? new Date(endDateStr + "T23:59:59") : horizon;
        const finalLimit = endLimit < horizon ? endLimit : horizon;

        const sessionsCol = collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS);
        let count = 0;

        // ─── Resolver matrículas ativas (evita query se o caller já forneceu) ───
        let enrollments = existingEnrollments;

        if (enrollments === null || enrollments === undefined) {
            // Só faz a query se o caller não informou (ex: extensão de horizonte)
            const enrollmentsQuery = query(
                collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS),
                where("idClass", "==", classId),
                where("status", "==", "active"),
                where("type", "==", "regular")
            );
            const enrollmentsSnap = await getDocs(enrollmentsQuery);
            enrollments = enrollmentsSnap.docs.map(d => {
                const data = d.data();
                return {
                    idEnrollment: d.id,
                    idMember: data.idMember,
                    memberName: data.memberName || "",
                    memberPhone: data.memberPhone || "",
                    memberEmail: data.memberEmail || "",
                    startDate: data.startDate || null
                };
            });
        }

        // Buscar photoUrl dos membros em batch (evita N queries individuais)
        const memberPhotoMap = new Map();
        const uniqueMemberIds = [...new Set(enrollments.map(e => e.idMember).filter(Boolean))];

        if (uniqueMemberIds.length > 0) {
            // Firestore 'in' aceita até 30 IDs por query
            const CHUNK_SIZE = 30;
            for (let i = 0; i < uniqueMemberIds.length; i += CHUNK_SIZE) {
                const chunk = uniqueMemberIds.slice(i, i + CHUNK_SIZE);
                const membersQuery = query(
                    collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS),
                    where("__name__", "in", chunk)
                );
                const membersSnap = await getDocs(membersQuery);
                membersSnap.docs.forEach(d => {
                    const mData = d.data();
                    memberPhotoMap.set(d.id, mData.photoUrl || null);
                });
            }
        }

        // Pré-processar: Map de idEnrollment → startDate (O(1) lookup, não O(N) find)
        const enrollStartDates = new Map();
        const activeAttendances = enrollments.map(e => {
            enrollStartDates.set(e.idEnrollment, e.startDate || null);
            return {
                idEnrollment: e.idEnrollment,
                idMember: e.idMember,
                memberName: e.memberName,
                memberPhone: e.memberPhone || "",
                memberEmail: e.memberEmail || "",
                memberPhotoUrl: memberPhotoMap.get(e.idMember) || null,
                type: "REGULAR",
                status: "BOOKED"
            };
        });

        // Encontra o primeiro dia da semana correto a partir do startDate
        let current = new Date(startDate);
        while (current.getDay() !== weekday) {
            current.setDate(current.getDate() + 1);
        }

        // Gera em lotes de até 500 (limite do Firestore batch)
        const BATCH_LIMIT = 499;
        let batch = writeBatch(db);
        let batchCount = 0;

        while (current <= finalLimit) {
            const sessionDate = toISODate(current);

            // Filtrar por startDate da matrícula (lookup O(1) via Map)
            const sessionAttendances = activeAttendances.filter(a => {
                const enrollStart = enrollStartDates.get(a.idEnrollment);
                return !enrollStart || enrollStart <= sessionDate;
            });

            const sessionData = {
                idTenant,
                idClass: classId,
                classGroupId: classData.classGroupId || "",
                idActivity: classData.idActivity || "",
                idEmployee: classData.idEmployee || "",
                idArea: classData.idArea || "",

                // Dados desnormalizados
                activityName: classData.activityName || "",
                activityColor: classData.activityColor || "#3b5de7",
                evaluationSystem: classData.evaluationSystem === true,
                employeeName: classData.employeeName || "",
                employeePhone: classData.employeePhone || null,
                employeeEmail: classData.employeeEmail || null,
                employeePhotoUrl: classData.employeePhotoUrl || null,

                // Substituição de Professor (Performance & Métricas)
                // Na geração: idOriginalEmployee = titular da turma.
                // isSubstitution = false (ainda não houve troca).
                idOriginalEmployee: classData.idEmployee || "",
                originalEmployeeName: classData.employeeName || "",
                originalEmployeePhotoUrl: classData.employeePhotoUrl || null,
                isSubstitution: false,

                areaName: classData.areaName || "",

                weekday,
                startTime: classData.startTime || "",
                endTime: classData.endTime || "",
                durationMinutes: Number(classData.durationMinutes) || 60,
                sessionDate,

                maxCapacity: Number(classData.maxCapacity) || 0,

                // Contadores derivados do array real
                attendances: sessionAttendances.map(a => ({ ...a })),
                enrolledCount: sessionAttendances.length,
                experimentalCount: 0,

                status: "scheduled",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            const newDocRef = doc(sessionsCol);
            batch.set(newDocRef, sessionData);
            count++;
            batchCount++;

            if (batchCount >= BATCH_LIMIT) {
                await batch.commit();
                batch = writeBatch(db);
                batchCount = 0;
            }

            // Próxima semana
            current.setDate(current.getDate() + 7);
        }

        // Commit final
        if (batchCount > 0) {
            await batch.commit();
        }

        return count;
    },

    /**
     * Busca sessões de um tenant dentro de um range de datas.
     * Usado pela Grade Operacional.
     * 
     * @param {string} idTenant
     * @param {string} startDate - ISO "YYYY-MM-DD"
     * @param {string} endDate - ISO "YYYY-MM-DD"
     * @returns {Array} Lista de sessões
     */
    getSessions: async (idTenant, startDate, endDate) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS),
                where("sessionDate", ">=", startDate),
                where("sessionDate", "<=", endDate),
                orderBy("sessionDate", "asc")
            );

            const querySnapshot = await getDocs(q);
            const sessions = [];
            querySnapshot.forEach((docSnap) => {
                sessions.push({ id: docSnap.id, ...docSnap.data() });
            });
            return sessions;
        } catch (error) {
            console.error("[SessionService] Erro ao buscar sessões:", error);
            throw error;
        }
    },

    /**
     * Remove sessões de uma turma a partir de uma data específica.
     * Usado ao EXCLUIR uma turma (só sessões futuras).
     * 
     * @param {string} idTenant
     * @param {string} idClass - ID da turma
     * @param {string} fromDate - ISO "YYYY-MM-DD" (inclusive)
     */
    deleteSessionsFromDate: async (idTenant, idClass, fromDate) => {
        if (!idTenant || !idClass) throw new Error("Tenant e Class ID são obrigatórios.");

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS),
                where("idClass", "==", idClass),
                where("sessionDate", ">=", fromDate)
            );

            const snapshot = await getDocs(q);
            if (snapshot.empty) return 0;

            const BATCH_LIMIT = 499;
            const docs = snapshot.docs;

            for (let i = 0; i < docs.length; i += BATCH_LIMIT) {
                const chunk = docs.slice(i, i + BATCH_LIMIT);
                const batch = writeBatch(db);
                chunk.forEach(d => batch.delete(d.ref));
                await batch.commit();
            }

            return docs.length;
        } catch (error) {
            console.error("[SessionService] Erro ao deletar sessões:", error);
            throw error;
        }
    },

    /**
     * Atualiza sessões de uma turma a partir de uma data específica.
     * Usado ao EDITAR uma turma (só sessões futuras).
     * 
     * @param {string} idTenant
     * @param {string} idClass - ID da turma
     * @param {string} fromDate - ISO "YYYY-MM-DD" (inclusive)
     * @param {Object} newData - Novos dados da turma para propagar
     */
    updateSessionsFromDate: async (idTenant, idClass, fromDate, newData) => {
        if (!idTenant || !idClass) throw new Error("Tenant e Class ID são obrigatórios.");

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS),
                where("idClass", "==", idClass),
                where("sessionDate", ">=", fromDate)
            );

            const snapshot = await getDocs(q);
            if (snapshot.empty) return 0;

            // Monta os campos atualizáveis (não atualiza sessionDate nem weekday)
            const updateFields = {
                ...(newData.idActivity !== undefined && { idActivity: newData.idActivity }),
                ...(newData.idEmployee !== undefined && { idEmployee: newData.idEmployee }),
                ...(newData.idArea !== undefined && { idArea: newData.idArea }),
                ...(newData.classGroupId !== undefined && { classGroupId: newData.classGroupId }),
                ...(newData.activityName !== undefined && { activityName: newData.activityName }),
                ...(newData.activityColor !== undefined && { activityColor: newData.activityColor }),
                ...(newData.evaluationSystem !== undefined && { evaluationSystem: newData.evaluationSystem === true }),
                ...(newData.employeeName !== undefined && { employeeName: newData.employeeName }),
                ...(newData.employeePhone !== undefined && { employeePhone: newData.employeePhone }),
                ...(newData.employeeEmail !== undefined && { employeeEmail: newData.employeeEmail }),
                ...(newData.employeePhotoUrl !== undefined && { employeePhotoUrl: newData.employeePhotoUrl }),
                // Quando a turma troca de professor (edição da turma), o novo professor vira o titular
                // de todas as sessões futuras. Diferente de substituição pontual (apenas 1 sessão).
                ...(newData.idEmployee !== undefined && { idOriginalEmployee: newData.idEmployee }),
                ...(newData.employeeName !== undefined && { originalEmployeeName: newData.employeeName }),
                ...(newData.employeePhotoUrl !== undefined && { originalEmployeePhotoUrl: newData.employeePhotoUrl }),
                ...(newData.idEmployee !== undefined && { isSubstitution: false }),
                ...(newData.areaName !== undefined && { areaName: newData.areaName }),
                ...(newData.startTime !== undefined && { startTime: newData.startTime }),
                ...(newData.endTime !== undefined && { endTime: newData.endTime }),
                ...(newData.durationMinutes !== undefined && { durationMinutes: Number(newData.durationMinutes) }),
                ...(newData.maxCapacity !== undefined && { maxCapacity: Number(newData.maxCapacity) }),
                updatedAt: serverTimestamp(),
            };

            const BATCH_LIMIT = 499;
            const docs = snapshot.docs;

            for (let i = 0; i < docs.length; i += BATCH_LIMIT) {
                const chunk = docs.slice(i, i + BATCH_LIMIT);
                const batch = writeBatch(db);
                chunk.forEach(d => batch.update(d.ref, updateFields));
                await batch.commit();
            }

            return docs.length;
        } catch (error) {
            console.error("[SessionService] Erro ao atualizar sessões:", error);
            throw error;
        }
    },

    /**
     * Busca a data da última sessão gerada para uma turma.
     * Usada pela Cloud Function para saber de onde continuar.
     * 
     * @param {string} idTenant
     * @param {string} idClass
     * @returns {string|null} ISO date da última sessão ou null
     */
    getLastSessionDate: async (idTenant, idClass) => {
        if (!idTenant || !idClass) return null;

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS),
                where("idClass", "==", idClass),
                orderBy("sessionDate", "desc"),
                limit(1)
            );

            const snapshot = await getDocs(q);
            if (snapshot.empty) return null;

            // Pegar a primeira (maior data) — como limitamos nenhuma, pegamos docs[0]
            return snapshot.docs[0].data().sessionDate || null;
        } catch (error) {
            console.error("[SessionService] Erro ao buscar última sessão:", error);
            return null;
        }
    },
};

export default SessionService;
